import { useCallback, useEffect, useState } from "react";
import useApi from "./useApi";
import { getAllSystemSettingNew } from "../services/systemSettingService";
import { POSTIF_SETTINGS, POSTIF_SETTING_DEFAULTS } from "../data/constants";
import { Setting } from "../types/Setting";

/**
 * Reads the System_Settings_New rows in the "PostIF" category.
 *
 * Not useSettingValue: SettingsProvider only loads the "Portal" category into the Redux
 * settings store, so a PostIF property is never found there and would silently fall back
 * to its default. Each getter falls back to the row's seeded DefaultValue, so an
 * environment that has not run the Epic 79819 seed still behaves sensibly.
 */
const usePifSettings = () => {
  const { executeApi } = useApi();
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      const response = await executeApi<string, Setting[]>(getAllSystemSettingNew, POSTIF_SETTINGS.CATEGORY);
      if (!active) return;
      if (response) setSettings(response);
      setLoaded(true);
    };

    void load();
    return () => {
      active = false;
    };
  }, [executeApi]);

  const rawValue = useCallback(
    (property: string): string | null => {
      const match = settings.find((s) => s.property === property);
      return match?.value ?? match?.defaultValue ?? null;
    },
    [settings],
  );

  const numberValue = useCallback(
    (property: string, fallback: number): number => {
      const parsed = Number(rawValue(property));
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    },
    [rawValue],
  );

  const booleanValue = useCallback(
    (property: string, fallback: boolean): boolean => {
      const raw = rawValue(property);
      return raw === null ? fallback : raw.trim().toLowerCase() === "true";
    },
    [rawValue],
  );

  return {
    loaded,
    /** Mismatch attempts before we advise contacting HR. */
    retryLimit: numberValue(POSTIF_SETTINGS.RETRY_LIMIT, POSTIF_SETTING_DEFAULTS.RETRY_LIMIT),
    /** When false, a personal email is required on the form. */
    emailRequired: !booleanValue(POSTIF_SETTINGS.ALLOW_WITHOUT_EMAIL, POSTIF_SETTING_DEFAULTS.ALLOW_WITHOUT_EMAIL),
    /** When false, a cell is required if there is no home number. */
    cellRequired: !booleanValue(POSTIF_SETTINGS.ALLOW_WITHOUT_CELL, POSTIF_SETTING_DEFAULTS.ALLOW_WITHOUT_CELL),
  };
};

export default usePifSettings;
